/**
 * Tuva v2 – shared theme utilities.
 *
 * Each app can import and use these directly, optionally overriding
 * DEFAULT_THEME for its own default.
 */

export const THEME_NAMES = ['pink', 'green', 'yellow', 'red', 'purple', 'espresso'];

export const NAVBAR_COLORS = {
  pink: '#8C1A64',
  green: '#004445',
  yellow: '#ecb606',
  red: '#a71931', // Tuva Jr.
  purple: '#682D8A', // Tuva Sims (base / nav bar) — matches sim-toolbar --ttb-bg
  espresso: '#5c4738', // Tuva Sims (base / nav bar) — brown espresso, sibling of purple
};

/**
 * Reads theme from app config or URL query param, with fallback.
 */
export function getTheme(appConfig, defaultTheme = 'green') {
  return (
    appConfig?.theme ||
    new URLSearchParams(window.location.search).get('theme') ||
    defaultTheme
  );
}

/**
 * Applies the theme by setting the data-theme attribute on <html>.
 */
export function applyTheme(themeName, defaultTheme = 'green') {
  const name = THEME_NAMES.includes(themeName) ? themeName : defaultTheme;
  document.documentElement.setAttribute('data-theme', name);
  return name;
}

/**
 * Returns the navbar background color for the given theme.
 *
 * Source of truth is server-side: settings.NAVBAR_COLORS, delivered as
 * `appConfig.navbar_colors` (v2 apps) or the header bootstrap. Pass it as
 * `overrideColors`. The hardcoded NAVBAR_COLORS map is the fallback for when the
 * server map is absent (standalone dev, or before app_config loads).
 */
export function getNavbarColor(themeName, defaultTheme = 'green', overrideColors = null) {
  const map =
    overrideColors && Object.keys(overrideColors).length
      ? overrideColors
      : NAVBAR_COLORS;
  return map[themeName] || map[defaultTheme] || NAVBAR_COLORS[defaultTheme];
}

/**
 * Toggles dark mode by setting data-dark attribute on <html>.
 * Returns the new dark mode state.
 */
export function toggleDarkMode() {
  const html = document.documentElement;
  const isDark = html.getAttribute('data-dark') === 'true';
  html.setAttribute('data-dark', String(!isDark));
  return !isDark;
}

/**
 * Returns whether dark mode is currently active.
 */
export function isDarkMode() {
  return document.documentElement.getAttribute('data-dark') === 'true';
}
